/**
 * FAQ content — one array, two consumers.
 *
 * @remarks
 * This is the worked example of a rule that matters more than it looks: **structured data must
 * describe content the visitor can actually see.** Google issues manual actions for `FAQPage`
 * markup whose questions are not on the page.
 *
 * The array below is rendered as visible `<details>` elements by `ContactView.vue` and is handed to
 * `seo.faqPage()` in `config/entities.ts` to produce the `FAQPage` entity. There is no second copy
 * to forget to update.
 *
 * Delete this file when forking if the site has no FAQ — see AGENTS.md § When Forking This Template.
 *
 * @module
 * @author Bayu Dwiyan Satria
 * @since 1.0.0
 */

/**
 * A single question and its answer.
 */
export interface FaqItem {
  /** The question, exactly as rendered. */
  readonly question: string
  /** The answer as plain text. Keep it free of markup — it is emitted verbatim into JSON-LD. */
  readonly answer: string
}

/**
 * The FAQ shown on the contact route and mirrored into `FAQPage` structured data.
 */
export const faqItems: readonly FaqItem[] = [
  {
    question: 'What does this boilerplate actually give me?',
    answer:
      'A Vue 3 application that prerenders every route to static HTML, emits per-page metadata and Schema.org structured data into that HTML, generates its own sitemap and robots.txt from the route table, and enforces its own layering rules through ESLint.'
  },
  {
    question: 'Why prerender instead of a plain single-page app?',
    answer:
      'A single-page app serves the same empty shell for every URL. Metadata written by JavaScript after load is unreliable for crawlers and useless for social-media unfurlers, which never execute scripts. Prerendering puts the correct title, canonical URL and JSON-LD in the bytes that come off the server.'
  },
  {
    question: 'Do I have to deploy to Cloudflare Pages?',
    answer:
      'No. The application core has no Cloudflare dependency and the build produces plain static files. The Cloudflare-specific pieces are confined to wrangler.json, the functions directory, and the _headers and _redirects files, all of which have documented equivalents for other static hosts.'
  },
  {
    question: 'How do I add a new page?',
    answer:
      'Add a view component, add one entry to the route table with its title, description and schema, and the sitemap, canonical URL and prerendered HTML file follow automatically. Nothing else needs editing.'
  }
]
